import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Link } from 'react-router';
import { ArrowLeft, FileText } from 'lucide-react';
import { toast } from 'sonner';

const invoiceSchema = z.object({
  objectType: z.string().min(1, 'Object type is required'),
  objectId: z.string().min(1, 'Object ID is required'),
  title: z.string().min(1, 'Title is required'),
  amount: z.string().min(1, 'Amount is required'),
});

type InvoiceForm = z.infer<typeof invoiceSchema>;

export function MFilesInvoicePage() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InvoiceForm>({
    resolver: zodResolver(invoiceSchema),
  });

  const upsertMutation = useMutation({
    mutationFn: (data: InvoiceForm) =>
      api.post('/api/connections/mfiles/upsertInvoice', data),
    onSuccess: () => {
      toast.success('Invoice upserted to M-Files successfully');
      reset();
    },
    onError: () => toast.error('Failed to upsert invoice to M-Files'),
  });

  async function onSubmit(data: InvoiceForm) {
    upsertMutation.mutate(data);
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/connections/mfiles">
          <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">M-Files Invoice</h1>
          <p className="text-gray-500">Upsert an invoice document to M-Files</p>
        </div>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" /> Upsert Invoice
          </CardTitle>
          <CardDescription>Create or update an invoice document in M-Files</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="objectType">Object Type</Label>
              <Input id="objectType" placeholder="Document" {...register('objectType')} />
              {errors.objectType && <p className="text-sm text-red-500">{errors.objectType.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="objectId">Object ID</Label>
              <Input id="objectId" placeholder="M-Files object ID" {...register('objectId')} />
              {errors.objectId && <p className="text-sm text-red-500">{errors.objectId.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input id="title" placeholder="Invoice title" {...register('title')} />
              {errors.title && <p className="text-sm text-red-500">{errors.title.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">Amount</Label>
              <Input id="amount" placeholder="0.00" {...register('amount')} />
              {errors.amount && <p className="text-sm text-red-500">{errors.amount.message}</p>}
            </div>
            <Button type="submit" disabled={upsertMutation.isPending}>
              {upsertMutation.isPending ? 'Upserting...' : 'Upsert to M-Files'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}